import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { getApiBaseUrl } from '@/lib/api'

const getAuthHeaders = async () => {
	const {
		data: { session },
	} = await supabase.auth.getSession()
	return {
		Authorization: `Bearer ${session?.access_token}`,
		'Content-Type': 'application/json',
	}
}

interface TeamData {
	team_id: string
	role: string
	id: string
	name: string
	slug: string
	plan_id: string
	created_at: string
	updated_at: string
}

export const useTeam = () => {
	const {
		data: teamData,
		isLoading,
		error,
		refetch,
	} = useQuery<TeamData | null, Error>({
		queryKey: ['team'],
		queryFn: async () => {
			const headers = await getAuthHeaders()
			const response = await fetch(`${getApiBaseUrl()}/api/team`, {
				headers,
			})

			if (!response.ok) {
				let errorJson = { error: 'Failed to fetch team data' }
				try {
					errorJson = await response.json()
				} catch (e) {
					// Ignore if parsing fails, use default error
				}

				// Specifically handle the "User is not part of any team" case
				// This is expected during onboarding, so return null instead of throwing
				if (
					response.status === 403
				) {
					return null // Indicate no team, not an error for React Query
				}
				throw new Error(errorJson.error || 'Failed to fetch team data')
			}

			// If response is OK but empty (should not happen with current backend logic for /api/team)
			const text = await response.text()
			if (!text) {
				return null // No team data, but not an error
			}
			return JSON.parse(text) // Backend returns the full team object
		},
		staleTime: 5 * 60 * 1000, // Cache for 5 minutes
		gcTime: 10 * 60 * 1000, // Keep unused data for 10 minutes
		refetchOnWindowFocus: false,
		retry: (failureCount, error: any) => {
			// Do not retry if it's the specific 403 error indicating no team
			if (
				error &&
				error.message &&
				error.message.includes('User is not part of any team')
			) {
				return false
			}
			// Default retry behavior for other errors (e.g., network issues)
			return failureCount < 1 // Or your preferred retry count
		},
	})

	return {
		teamId: teamData?.team_id ?? null,
		role: teamData?.role ?? null,
		teamData: teamData, // Expose the full team data object
		isLoading,
		error: error, // Keep the error object for other types of errors
		refetch, // Add refetch function for manual refresh
	}
}
